import { uuid } from 'vue-uuid';
import {
  TABLE_REF,
  SCALE_FLOORS,
  TableScale,
  tableMetrics,
  scaledMetrics,
  getTableContentScale,
} from '@/components/DesignObjects/TableScale';

const TABLE_DEFAULT_BORDER_WIDTH = 5;
function makeCell(text = '', fontSize = TABLE_REF.fontSize) {
  return {
    text,
    bold:       false,
    italic:     false,
    underline:  false,
    align:      'center',
    vAlign:     'middle',
    color:      '#000000',
    fill:       null, // background fill; null → no fill
    fontSize,
    fontFamily: 'Arial',
    lineHeight: '1.4',
    colSpan:    1,
    rowSpan:    1,
    hidden:     false,
  };
}
function cloneCellStyles(cell, fontSize = TABLE_REF.fontSize) {
  const next = makeCell('', (cell && cell.fontSize) || fontSize);
  if (!cell) { return next; }
  next.bold       = !!cell.bold;
  next.italic     = !!cell.italic;
  next.underline  = !!cell.underline;
  next.align      = cell.align || 'center';
  next.vAlign     = cell.vAlign || 'middle';
  next.color      = cell.color || '#000000';
  next.fill         = cell.fill || '#ffffff';
  next.fontSize   = cell.fontSize || fontSize;
  next.fontFamily = cell.fontFamily || 'Arial';
  next.lineHeight = cell.lineHeight != null ? cell.lineHeight : '1.4';
  next.text       = '';
  next.colSpan    = 1;
  next.rowSpan    = 1;
  next.hidden     = false;
  return next;
}
function shouldDrawVerticalLine(index, colCount, style) {
  const isLeft = index === 0;
  const isRight = index === colCount;
  const isInner = !isLeft && !isRight;
  switch (style) {
    case 'none':
    case 'top':
    case 'bottom':
    case 'horizontal-inner':
      return false;
    case 'outer': return isLeft || isRight;
    case 'inner':
    case 'vertical-inner':
      return isInner;
    case 'left': return isLeft;
    case 'right': return isRight;
    default: return true; // all
  }
}

function shouldDrawHorizontalLine(index, rowCount, style) {
  const isTop = index === 0;
  const isBottom = index === rowCount;
  const isInner = !isTop && !isBottom;
  switch (style) {
    case 'none':
    case 'left':
    case 'right':
    case 'vertical-inner':
      return false;
    case 'outer': return isTop || isBottom;
    case 'inner':
    case 'horizontal-inner':
      return isInner;
    case 'top': return isTop;
    case 'bottom': return isBottom;
    default: return true; // all
  }
}

function cellSideBorders(r, c, rowCount, colCount, style) {
  const none = {
    top: false, right: false, bottom: false, left: false,
  };
  if (style === 'none') return none;
  const lastR = rowCount - 1;
  const lastC = colCount - 1;
  const sides = { ...none };
  if (style === 'all' || style === 'outer') {
    if (r === 0) sides.top = true;
    if (r === lastR) sides.bottom = true;
    if (c === 0) sides.left = true;
    if (c === lastC) sides.right = true;
  }
  if (style === 'all' || style === 'inner' || style === 'horizontal-inner') {
    if (r > 0) sides.top = true;
    if (r < lastR) sides.bottom = true;
  }
  if (style === 'all' || style === 'inner' || style === 'vertical-inner') {
    if (c > 0) sides.left = true;
    if (c < lastC) sides.right = true;
  }
  if (style === 'top' && r === 0) sides.top = true;
  if (style === 'bottom' && r === lastR) sides.bottom = true;
  if (style === 'left' && c === 0) sides.left = true;
  if (style === 'right' && c === lastC) sides.right = true;
  return sides;
}

function measureTableCellContentHeight(cell, widthPx, tableFontFamily, textOverride, metrics) {
  const m          = metrics || tableMetrics(null);
  const width      = Math.max(1, Math.round(widthPx) || 1);
  const probe      = document.createElement('div');
  const fontFamily = (cell && cell.fontFamily) || tableFontFamily || 'Arial';
  const fontSize   = (cell && cell.fontSize) || m.fontSize;
  const lineHeight = cell && cell.lineHeight != null ? cell.lineHeight : 1.4;
  const text       = textOverride != null ? textOverride : ((cell && cell.text) || '');

  probe.style.cssText = [
    'position:absolute',
    'left:-99999px',
    'top:0',
    'visibility:hidden',
    'pointer-events:none',
    'height:auto',
    'min-height:0',
    'max-height:none',
    'overflow:visible',
    `width:${width}px`,
    `padding:${m.padY}px ${m.padX}px`,
    'box-sizing:border-box',
    'white-space:pre-wrap',
    'word-break:break-word',
    `font-family:"${String(fontFamily).replace(/"/g, '')}"`,
    `font-size:${fontSize}px`,
    `line-height:${lineHeight}`,
    `font-weight:${cell && cell.bold ? 700 : 400}`,
    `font-style:${cell && cell.italic ? 'italic' : 'normal'}`,
  ].join(';');
  probe.textContent   = text;

  document.body.appendChild(probe);
  const height = probe.scrollHeight;
  document.body.removeChild(probe);
  return height;
}

function getTableRowContentMinHeight(table, rowIndex, liveTextByCol = null) {
  const metrics  = tableMetrics(table);
  const rowCells = table && table.cells && table.cells[rowIndex];
  const cols     = (table && table.cols) || [];
  if (!rowCells || !rowCells.length) {
    return metrics.minRowHeight;
  }

  let maxNeeded = 0;
  rowCells.forEach((cell, c) => {
    if (!cell || cell.hidden) { return; }
    const width    = (cols[c] && cols[c].width) || metrics.minColWidth;
    const liveText = liveTextByCol && Object.prototype.hasOwnProperty.call(liveTextByCol, c)
        ? liveTextByCol[c]
        : null;
    maxNeeded      = Math.max(
        maxNeeded,
        measureTableCellContentHeight(cell, width, table.fontFamily, liveText, metrics),
    );
  });

  return Math.max(metrics.minRowHeight, maxNeeded);
}
function isCornerResize(direction) {
  return !!direction && direction[0] !== 0 && direction[1] !== 0;
}

function tableEdgeIndex(direction, axis, count) {
  if (!direction || !direction[axis] || !count) return -1;
  return direction[axis] === -1 ? 0 : count - 1;
}

function tableColWidths(table) {
  return (table.cols || []).map(col => col.width || 0);
}

function tableRowHeights(table) {
  return (table.rows || []).map(row => row.height || 0);
}

function tableColMins(table) {
  const { minColWidth } = tableMetrics(table);
  return (table.cols || []).map(() => minColWidth);
}

function tableRowMins(table) {
  return (table.rows || []).map((row, r) => getTableRowContentMinHeight(table, r));
}

function resizeTableTrack(sizes, mins, total, index, corner) {
  const sum = sizes.reduce((s, v) => s + v, 0);
  if (corner) {
    return sizes.map((v, i) => Math.max(mins[i], Math.round((v * total) / (sum || 1))));
  }
  if (index < 0) return sizes.slice();
  const next = sizes.slice();
  next[index] = Math.max(mins[index], sizes[index] + (total - sum));
  return next;
}

function tableTrackMin(sizes, mins, index, corner) {
  if (corner) return mins.reduce((s, v) => s + v, 0);
  if (index < 0) return 0;
  return sizes.reduce((s, v) => s + v, 0) - sizes[index] + mins[index];
}

function tableUniformMinRatio(table) {
  const fontFallback = tableMetrics(table).fontSize;
  const fonts        = [];
  (table.cells || []).forEach((row) => {
    (row || []).forEach((cell) => {
      if (cell && !cell.hidden) { fonts.push(cell.fontSize || fontFallback); }
    });
  });

  const floorRatio = (floor, values) => {
    const live = values.filter(v => v > 0);
    return live.length ? floor / Math.min(...live) : 0;
  };

  return Math.min(1, Math.max(
      floorRatio(SCALE_FLOORS.colWidth, tableColWidths(table)),
      floorRatio(SCALE_FLOORS.rowHeight, tableRowHeights(table)),
      floorRatio(SCALE_FLOORS.fontSize, fonts),
  ));
}

function scaleTableUniformly(table, ratio, setProp) {
  const r = ratio > 0 ? ratio : 1;
  if (r === 1) { return; }

  (table.cols || []).forEach((col) => { col.width = (col.width || 0) * r; });
  (table.rows || []).forEach((row) => { row.height = (row.height || 0) * r; });
  (table.cells || []).forEach((row) => {
    (row || []).forEach((cell) => {
      if (!cell) { return; }
      cell.fontSize = Math.max(SCALE_FLOORS.fontSize, (cell.fontSize || 0) * r);
    });
  });

  const contentScale = getTableContentScale(table) * r;
  if (setProp) {
    setProp(table, 'contentScale', contentScale);
  } else {
    table.contentScale = contentScale;
  }
  table.Width  = (table.cols || []).reduce((s, col) => s + (col.width || 0), 0);
  table.Height = (table.rows || []).reduce((s, row) => s + (row.height || 0), 0);
}
function cloneBorderLine(line) {
  return Object.assign({}, makeBorderLine(), line || {});
}
const TABLE_FILL_NONE  = 'none';
const TABLE_FILL_COLOR = 'color';
const TABLE_FILL_TYPES = [TABLE_FILL_NONE, TABLE_FILL_COLOR];

function makeFill(type = TABLE_FILL_NONE) {
  return {
    type,
    color: 'rgba(255,255,255,1)',
  };
}

function cloneFill(fill) {
  return fill ? Object.assign({}, fill) : null;
}

// Fills read off a cell can be missing (older tables) or carry a type this
// build no longer paints, so every reader goes through here.
function normalizeFill(fill) {
  const next = makeFill();
  if (fill && TABLE_FILL_TYPES.includes(fill.type)) { next.type = fill.type; }
  if (fill && typeof fill.color === 'string') { next.color = fill.color; }
  return next;
}

function fillIsPainted(fill) {
  return !!(fill && fill.type && fill.type !== TABLE_FILL_NONE);
}

function fillCssBackground(fill) {
  return fillIsPainted(fill) ? (fill.color || null) : null;
}

// Two fills look the same when this matches.
function fillIdentity(fill) {
  const next = normalizeFill(fill);
  return fillIsPainted(next) ? `color:${next.color}` : TABLE_FILL_NONE;
}

function getCellFill(table, r, c) {
  const cell = table && table.cells && table.cells[r] && table.cells[r][c];
  return cell ? cell.fill : null;
}

// Every cell of the table that can carry a fill (merged-away cells cannot).
function tableCellCoords(table) {
  const coords = [];
  ((table && table.cells) || []).forEach((row, r) => {
    (row || []).forEach((cell, c) => {
      if (cell && !cell.hidden) { coords.push({ r, c }); }
    });
  });
  return coords;
}

// The one fill all the given cells share, or null when they differ.
function sharedCellsFill(table, cells) {
  if (!table || !cells || !cells.length) { return null; }
  let shared = null;
  let key    = null;
  for (let i = 0; i < cells.length; i += 1) {
    const fill = normalizeFill(getCellFill(table, cells[i].r, cells[i].c));
    const id   = fillIdentity(fill);
    if (key === null) {
      shared = fill;
      key    = id;
    } else if (id !== key) {
      return null;
    }
  }
  return shared;
}

function applyFillToCells(table, cells, fill, setItem) {
  if (!table || !table.cells) { return; }
  (cells || []).forEach(({ r, c }) => {
    const row  = table.cells[r];
    const cell = row && row[c];
    if (!cell) { return; }
    const next = cloneFill(fill);
    if (setItem) { setItem(cell, 'fill', next); } else { cell.fill = next; }
  });
}

function makeBorderLine() {
  return {
    color: '#000000',
    width: TABLE_DEFAULT_BORDER_WIDTH,
    dash:  'solid', // none | solid | dash | dash-short | dotted
  };
}

function defaultBorderLines(rowCount, colCount) {
  return {
    vBorders: Array.from({ length: colCount + 1 }, () =>
        Array.from({ length: rowCount }, () => makeBorderLine())
    ),
    hBorders: Array.from({ length: rowCount + 1 }, () =>
        Array.from({ length: colCount }, () => makeBorderLine())
    ),
  };
}
function ensureSegmentBorders(table) {
  ensureTableBorders(table);
  const cols = (table.cols || []).length;
  const rows = (table.rows || []).length;
  const v = table.vBorders || [];
  if (v[0] && !Array.isArray(v[0])) {
    table.vBorders = v.map(line =>
        Array.from({ length: rows }, () => Object.assign({}, line))
    );
  }
  const h = table.hBorders || [];
  if (h[0] && !Array.isArray(h[0])) {
    table.hBorders = h.map(line =>
        Array.from({ length: cols }, () => Object.assign({}, line))
    );
  }
}
function cellSideKeys(target) {
  if (Array.isArray(target)) {
    return target.filter(s => ['top', 'bottom', 'left', 'right'].includes(s));
  }
  switch (target) {
    case 'top': return ['top'];
    case 'bottom': return ['bottom'];
    case 'left': return ['left'];
    case 'right': return ['right'];
    case 'outer':
    case 'all':
    default: return ['top', 'right', 'bottom', 'left'];
  }
}

function cellEdgeRef(table, r, c, side) {
  if (!table || !table.vBorders || !table.hBorders) return null;
  if (side === 'left')   return table.vBorders[c]     ? { arr: table.vBorders[c],     i: r } : null;
  if (side === 'right')  return table.vBorders[c + 1] ? { arr: table.vBorders[c + 1], i: r } : null;
  if (side === 'top')    return table.hBorders[r]     ? { arr: table.hBorders[r],     i: c } : null;
  if (side === 'bottom') return table.hBorders[r + 1] ? { arr: table.hBorders[r + 1], i: c } : null;
  return null;
}

// The border buttons that stand for a set of edges rather than a single one.
const CELL_BORDER_GROUPS = {
  all:   ['top', 'bottom', 'left', 'right', 'horizontal-inner', 'vertical-inner'],
  outer: ['top', 'bottom', 'left', 'right'],
  inner: ['horizontal-inner', 'vertical-inner'],
};

// The edge names a border button covers, e.g. "inner" -> both inner directions.
function cellBorderTargetNames(target) {
  const names = [];
  (Array.isArray(target) ? target : [target]).forEach((t) => {
    (CELL_BORDER_GROUPS[t] || [t]).forEach((name) => {
      if (!names.includes(name)) {
        names.push(name);
      }
    });
  });
  return names;
}

// Splits the edges of a cell selection into its outline and the divides inside it.
// An edge between two selected cells is inner; every other edge is on the outline.
function cellSelectionEdges(table, cells) {
  const picked = new Set(cells.map(({ r, c }) => `${r}:${c}`));
  const edges  = {};

  cells.forEach(({ r, c }) => {
    const add = (name, side) => {
      edges[name] = (edges[name] || []).concat(cellEdgeRef(table, r, c, side));
    };
    // An inner edge is collected once, from the lower/right cell of the pair.
    add(picked.has(`${r - 1}:${c}`) ? 'horizontal-inner' : 'top', 'top');
    add(picked.has(`${r}:${c - 1}`) ? 'vertical-inner' : 'left', 'left');
    if (!picked.has(`${r + 1}:${c}`)) {
      add('bottom', 'bottom');
    }
    if (!picked.has(`${r}:${c + 1}`)) {
      add('right', 'right');
    }
  });
  return edges;
}

// Every existing border line the given buttons point at, for the selected cells.
function cellSelectionEdgeRefs(table, cells, target) {
  if (!table || !table.vBorders || !table.hBorders) {
    return [];
  }
  if (!cells || !cells.length) {
    return [];
  }

  const edges = cellSelectionEdges(table, cells);
  return cellBorderTargetNames(target)
      .reduce((refs, name) => refs.concat(edges[name] || []), [])
      .filter(ref => ref && ref.arr && ref.arr[ref.i] != null);
}

function applyToSelectedCellBorders(table, cells, target, patch, setItem) {
  cellSelectionEdgeRefs(table, cells, target).forEach((ref) => {
    const next = Object.assign({}, ref.arr[ref.i], patch);
    if (setItem) {
      setItem(ref.arr, ref.i, next);
    } else {
      ref.arr.splice(ref.i, 1, next);
    }
  });
}
function getTableBorderTargets(table) {
  if (!table) return ['all'];
  if (Array.isArray(table.borderTargets) && table.borderTargets.length) {
    return table.borderTargets.slice();
  }
  return [table.borderStyle || 'all'];
}

function normalizeBorderTargets(target, table) {
  if (Array.isArray(target)) {
    return target.filter(Boolean);
  }
  if (target) return [target];
  return getTableBorderTargets(table);
}

function lineMatchesBorderTargets(axis, index, count, targets) {
  if (!targets || !targets.length) return false;
  if (targets.includes('all')) return true;
  const check = axis === 'v' ? shouldDrawVerticalLine : shouldDrawHorizontalLine;
  return targets.some(style => check(index, count, style));
}

function getTargetBorderLines(table, target) {
  if (!table) return [];
  const styles = normalizeBorderTargets(target, table);
  const cols = (table.cols || []).length;
  const rows = (table.rows || []).length;
  const v = table.vBorders || [];
  const h = table.hBorders || [];
  const list = [];

  v.forEach((segments, i) => {
    if (!lineMatchesBorderTargets('v', i, cols, styles)) return;
    const segs = Array.isArray(segments) ? segments : [segments];
    segs.forEach((line, r) => {
      list.push({
        line, axis: 'v', i, r,
      });
    });
  });

  h.forEach((segments, i) => {
    if (!lineMatchesBorderTargets('h', i, rows, styles)) return;
    const segs = Array.isArray(segments) ? segments : [segments];
    segs.forEach((line, c) => {
      list.push({
        line, axis: 'h', i, c,
      });
    });
  });

  return list;
}

function applyToTargetBorders(table, patch, target, setItem) {
  getTargetBorderLines(table, target).forEach(({ line, axis, i, r, c }) => {
    const next = Object.assign({}, line, patch);
    if (axis === 'v' && table.vBorders[i]) {
      if (setItem) setItem(table.vBorders[i], r, next);
      else table.vBorders[i].splice(r, 1, next);
    }
    if (axis === 'h' && table.hBorders[i]) {
      if (setItem) setItem(table.hBorders[i], c, next);
      else table.hBorders[i].splice(c, 1, next);
    }
  });
}

function sharedTargetValue(table, key, target) {
  const lines = getTargetBorderLines(table, target).map(({ line }) => line[key]);
  if (!lines.length) return null;
  return lines.every(v => v === lines[0]) ? lines[0] : null; // null → show "--"
}
function ensureTableBorders(table) {
  if (!table) return;
  const colCount = (table.cols || []).length;
  const rowCount = (table.rows || []).length;
  const needV = !table.vBorders || table.vBorders.length !== colCount + 1;
  const needH = !table.hBorders || table.hBorders.length !== rowCount + 1;
  if (!needV && !needH) return;
  const fresh = defaultBorderLines(rowCount, colCount);
  if (needV) table.vBorders = fresh.vBorders;
  if (needH) table.hBorders = fresh.hBorders;
}

export default class TableObject {
  data = {
    AllowMove:               'true',
    AllowResize:             'true',
    AllowRotate:             'true',
    Height:                  176,
    Width:                   330,
    Id:                      uuid.v1(),
    IsEditable:              'true',
    IsVisible:               true,
    Name:                    'Table 1',
    PageNo:                  0,
    PdfSortOrder:            '1',
    SortOrder:               '1',
    RotateDegree:            0,
    X:                       0,
    Y:                       0,
    type:                    'table',
    scaleX:                  1,
    scaleY:                  1,
    locked:                  false,
    isSelected:              false,
    isEditableForRightPanel: false,
    elementIndex:            -1,
    cols:                    [],
    rows:                    [],
    cells:                   [],
    showBorders:             true,
    borderColor:             '#000000',
    borderWidth:             TABLE_DEFAULT_BORDER_WIDTH,
    fontFamily:              'Arial',
    animation:               {
      speed:     1, style:     '', startTime: 0.0, duration:  0.0, onEnter:   false, onExit:    false,
    },
    isAnimated: false,
    borderStyle:   'all',
    borderTargets: ['all'],
    borderEpoch:   0,
    fillEpoch:     0,
    pageScale:     1,
    contentScale:  1,
    vBorders:      [], // one entry per vertical grid line (cols + 1)
    hBorders:      [], // one entry per horizontal grid line (rows + 1)
  };

  constructor(rowCount = 4, colCount = 3, pageWidth = 612, pageHeight = 792) {
    const scale = TableScale(pageWidth, pageHeight, rowCount, colCount);
    const m     = scaledMetrics(scale);

    this.data.pageScale    = scale;
    this.data.contentScale = scale;
    this.data.cols         = Array.from({ length: colCount }, () => ({ width: m.colWidth }));
    this.data.rows         = Array.from({ length: rowCount }, () => ({ height: m.rowHeight }));
    const lines            = defaultBorderLines(rowCount, colCount);
    this.data.vBorders     = lines.vBorders;
    this.data.hBorders     = lines.hBorders;
    this.data.cells        = [];
    for (let r = 0; r < rowCount; r++) {
      const row = [];
      for (let c = 0; c < colCount; c++) {
        row.push(makeCell('', m.fontSize));
      }
      this.data.cells.push(row);
    }
    this.data.Width  = this.data.cols.reduce((sum, col) => sum + col.width, 0);
    this.data.Height = this.data.rows.reduce((sum, row) => sum + row.height, 0);
  }
}

export {
  makeCell,
  TABLE_FILL_NONE,
  TABLE_FILL_COLOR,
  TABLE_FILL_TYPES,
  makeFill,
  cloneFill,
  normalizeFill,
  fillIsPainted,
  fillCssBackground,
  fillIdentity,
  getCellFill,
  tableCellCoords,
  sharedCellsFill,
  applyFillToCells,
  measureTableCellContentHeight,
  getTableRowContentMinHeight,
  shouldDrawVerticalLine,
  shouldDrawHorizontalLine,
  cellSideBorders,
  defaultBorderLines,
  applyToTargetBorders,
  sharedTargetValue,
  getTableBorderTargets,
  ensureTableBorders,
  makeBorderLine,
  ensureSegmentBorders,
  getTargetBorderLines,
  applyToSelectedCellBorders,
  cellSelectionEdgeRefs,
  cellBorderTargetNames,
  cellEdgeRef,
  cellSideKeys,
  TABLE_DEFAULT_BORDER_WIDTH,
  isCornerResize,
  tableEdgeIndex,
  tableColWidths,
  tableRowHeights,
  tableColMins,
  tableRowMins,
  resizeTableTrack,
  tableTrackMin,
  tableUniformMinRatio,
  scaleTableUniformly,
  cloneBorderLine,
  cloneCellStyles
};
